/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { BackupEnum } from '../enums/api';
import { ErrorResponseModel } from '../../../../core/models';
import { HealthCheckModel } from '../models/healthcheck.model';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';

@Injectable()
export class HealthChecksApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all healthchecks.
     * @param options
     */
    public getAllHealthChecks(options?: any): Observable<any> {
        return new Observable((observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.get(
                    BackupEnum.GET_ALL_EXTERNAL_CHECK_SCRIPTS,
                    moduleAuth,
                    false
                ).subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
            }
        );
    }

    /**
     * Get all healthchecks for desired service.
     * @param service
     * @param options
     */
    public getHealthChecksByService(service: string, options?: any): Observable<any> {
        return new Observable((observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.get(BackupEnum.GET_EXTERNAL_SCRIPTS_BY_SERVICE(service), moduleAuth, false).subscribe(
                    data => {
                        observer.next(data);
                        observer.complete();
                    },
                    error => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get individual health check by ID.
     * @param id
     * @param options
     */
    public getHealthCheckById(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(
                BackupEnum.GET_EXTERNAL_SCRIPT_BY_ID(id),
                moduleAuth,
                false
            ).subscribe(
                response => {
                    observer.next(response);
                    observer.complete();
                },
                error => {
                    observer.next(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Upload a custom health check script.
     * @param form
     * @param options
     */
    public addHealthCheck(form: HealthCheckModel, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(
                BackupEnum.POST_HEALTHCHECK_SCRIPT,
                form,
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Edit selected health check script.
     * @param id
     * @param data
     * @param options
     */
    public editHealthCheck(id: string, data: any, options?: any): Observable<any> {
        return new Observable<any>((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(
                BackupEnum.PUT_EXTERNAL_CHECK_SCRIPTS(id),
                data,
                moduleAuth,
            ).subscribe(
                response => {
                    observer.next(response);
                    observer.complete();
                },
                error => {
                    observer.next(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Delete health check script.
     * @param id
     * @param options
     */
    public deleteHealthCheck(id: string, options?: any): Observable<any> {
        return new Observable<any>((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(
                BackupEnum.DELETE_EXTERNAL_CHECK_SCRIPTS(id),
                moduleAuth,
            ).subscribe(
                response => {
                    observer.next(response);
                    observer.complete();
                },
                error => {
                    observer.next(error);
                    observer.complete();
                }
            );
        });
    }
}
